import type { ColumnDef } from "@tanstack/react-table";
import type React from "react";
import { useEffect, useId, useMemo } from "react";
import { buildNonEntityColumn } from "./functions";
import { useHyperTable } from "./HyperTable";
import type { ColumnExtras, Column as ColumnType } from "./types";

export type NonEntityColumnProps<Entity> = Partial<ColumnDef<Entity, unknown>> &
  ColumnExtras<Entity> & {
    caption?: React.ReactNode;
    children?: React.ReactNode;
  };

export const NonEntityColumn = <Entity extends any>(
  props: NonEntityColumnProps<Entity>,
) => {
  const { children, caption, deps = [], ...override } = props;
  const ctx = useHyperTable<Entity>();
  const _id = useId();
  const id = override.id ?? _id;

  const column = useMemo<ColumnType<Entity>>(
    () =>
      buildNonEntityColumn({
        caption: caption ?? children ?? null,
        id,
        ...override,
      }),
    [JSON.stringify(override), caption, children, ...deps],
  );

  useEffect(() => {
    ctx.columnActions.add(column);
    return () => {
      ctx.columnActions.remove(id);
    };
  }, []);

  useEffect(() => {
    ctx.columnActions.update(id, () => column);
  }, [column]);

  return null;
};